import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { merge, fromEvent } from "rxjs";
import { filter, map } from "rxjs/operators";

import { ClipboardActions } from "./clipboard.actions";
import { CLIPBOARD_STORAGE_KEY, CLIPBOARD_EVENT_NAME } from "./clipboard.state";
import { CopiedEntityData } from "src/app/shared/interfaces/interfaces";
import { UserActions } from "../../user/user.actions";

@Injectable()
export class ClipboardEffects {
    private readonly actions$ = inject(Actions);

    // =========================================================================
    // INITIALIZE CLIPBOARD FROM LOCALSTORAGE
    // =========================================================================
    initializeClipboard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.initializeUserSuccess, UserActions.changeCompanySuccess),
            map(() => {
                let copiedEntity: CopiedEntityData | null = null;
                try {
                    const stored = localStorage.getItem(CLIPBOARD_STORAGE_KEY);
                    copiedEntity = stored ? JSON.parse(stored) : null;
                } catch {
                    copiedEntity = null;
                }
                return ClipboardActions.update({ data: copiedEntity });
            }),
        ),
    );

    // =========================================================================
    // CLIPBOARD SYNC ACROSS TABS
    // =========================================================================
    listenToClipboardChanges$ = createEffect(() =>
        merge(
            // Storage events from OTHER tabs
            fromEvent<StorageEvent>(window, "storage").pipe(
                filter(event => event.key === CLIPBOARD_STORAGE_KEY),
                map(event => {
                    if (event.newValue) {
                        try {
                            return JSON.parse(event.newValue);
                        } catch {
                            return null;
                        }
                    }
                    return null;
                }),
            ),
            // Custom events from SAME tab
            fromEvent<CustomEvent>(window, CLIPBOARD_EVENT_NAME).pipe(
                filter(event => event.detail?.key === CLIPBOARD_STORAGE_KEY),
                map(event => event.detail?.value || null),
            ),
        ).pipe(map(data => ClipboardActions.update({ data }))),
    );
}
